/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, April 27th 2023, 9:06:14 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import ApiIcon from "@mui/icons-material/Api";
import { IntegrationConnection, TaskTemplate } from "lib/schemas";
import { useIntegrationsAPI } from "integrations/services/IntegrationsAPI";
import { IntegrationConnectionsList, IntegrationConnectionForm } from "integrations/components";
import { useWorkflowContext } from "service-builder/providers";
import { StyledOSDialog } from "ui-widgets";
import { FormSubmitBubblingStopper } from "ui-widgets/forms";

interface WorkflowTaskIntegrationsProps
  extends Pick<React.ComponentProps<typeof IntegrationConnectionForm>, "open" | "onClose"> {
  taskTemplate: TaskTemplate;
}

export const WorkflowTaskIntegrations = ({ open, onClose, taskTemplate }: WorkflowTaskIntegrationsProps) => {
  const { useAPIListIntegrationConnections, useAPIListIntegrationProviders } = useIntegrationsAPI();
  const { fetchConnections, integrationConnections } = useAPIListIntegrationConnections();
  const { integrationProviders, fetchProviders } = useAPIListIntegrationProviders();

  React.useEffect(() => {
    if (taskTemplate?.id) {
      fetchProviders();
      fetchConnections({ taskTemplateConnectionIds: taskTemplate.integration_connections });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskTemplate?.id, taskTemplate?.integration_connections?.length, fetchConnections, fetchProviders]);

  const taskTemplateIntegrationConnections = integrationConnections?.filter((connection) =>
    taskTemplate?.integration_connections?.includes(connection.id)
  );

  const {
    taskTemplateListContext: { updateList },
    patchTaskTemplate,
  } = useWorkflowContext();

  const onSaveIntegrationConnection = (connection: IntegrationConnection) => {
    updateList({
      ...taskTemplate,
      integration_connections: [...new Set([...taskTemplate.integration_connections, connection.id])],
    });
  };

  const onDeleteConnection = (connectionId: IntegrationConnection["id"]) => {
    updateList({
      ...taskTemplate,
      integration_connections: taskTemplate.integration_connections.filter((v) => v !== connectionId),
    });
  };

  return (
    <FormSubmitBubblingStopper>
      <StyledOSDialog
        open={open}
        onClose={onClose}
        popUpTitle={`${taskTemplate?.title} Integrations`}
        popUpIcon={<ApiIcon color="secondary" />}
      >
        <IntegrationConnectionsList
          onSaveIntegrationConnection={onSaveIntegrationConnection}
          taskTemplateTitle={taskTemplate?.title}
          taskTemplateIds={[taskTemplate.id]}
          integrationConnections={taskTemplateIntegrationConnections}
          onAddExistingConnection={(connection) => {
            patchTaskTemplate({
              apiPathParams: [taskTemplate.id],
              body: JSON.stringify({
                integration_connections: [...taskTemplate.integration_connections, connection.id],
              }),
              setValidAPIResponse: (t) => onSaveIntegrationConnection(connection),
            });
          }}
          onDetachConnection={(connectionId) => {
            patchTaskTemplate({
              apiPathParams: [taskTemplate.id],
              body: JSON.stringify({
                integration_connections: taskTemplate.integration_connections.filter((v) => v !== connectionId),
              }),
              setValidAPIResponse: (t) => onDeleteConnection(connectionId),
            });
          }}
          onDeleteConnection={onDeleteConnection}
          integrationProviders={integrationProviders}
        />
      </StyledOSDialog>
    </FormSubmitBubblingStopper>
  );
};
